import React from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, Spinner, Form, Pagination } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowDown, ArrowUp, Truck } from 'react-bootstrap-icons';

// Actions
import { fetchDeliveries } from '../store/slices/deliverySlice';

const Deliveries = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { deliveries = [], loading } = useSelector((state) => state.deliveries);
  const [sortField, setSortField] = React.useState('expected_date');
  const [sortDirection, setSortDirection] = React.useState('asc');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [filterStatus, setFilterStatus] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');

  const itemsPerPage = 10;

  React.useEffect(() => {
    dispatch(fetchDeliveries());
  }, [dispatch]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort deliveries
  const filteredDeliveries = deliveries
    .filter(delivery => {
      if (!filterStatus) return true;
      return delivery.status === filterStatus;
    })
    .filter(delivery => {
      if (!searchTerm) return true;
      return (
        delivery.id.toString().includes(searchTerm) ||
        delivery.metal_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        delivery.tracking_number?.includes(searchTerm)
      );
    })
    .sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'expected_date':
          comparison = new Date(a.expected_date) - new Date(b.expected_date);
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'metal_type':
          comparison = a.metal_type.localeCompare(b.metal_type);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // Pagination
  const totalPages = Math.ceil(filteredDeliveries.length / itemsPerPage);
  const paginatedDeliveries = filteredDeliveries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Generate pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  // Get status badge
  const getStatusBadge = (status) => {
    let variant;
    let label = status.replace('_', ' ');

    switch(status) {
      case 'delivered':
        variant = 'success';
        break;
      case 'in_transit':
        variant = 'info';
        break;
      case 'pending':
        variant = 'warning';
        break;
      case 'delayed':
        variant = 'danger';
        break;
      default:
        variant = 'secondary';
    }

    return <Badge bg={variant} pill>{label}</Badge>;
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold">Deliveries</h2>
          <p className="text-muted">Track and manage your precious metals deliveries</p>
        </Col>
      </Row>

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3 mb-md-0">
                    <Form.Label>Filter by Status</Form.Label>
                    <Form.Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="delivered">Delivered</option>
                      <option value="in_transit">In Transit</option>
                      <option value="pending">Pending</option>
                      <option value="delayed">Delayed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Search Deliveries</Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type="text"
                        placeholder="Search by ID, metal type, or tracking number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <Button variant="outline-secondary">
                        <Search />
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body className="p-0">
              {filteredDeliveries.length > 0 ? (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSort('id')}
                      >
                        ID
                        {sortField === 'id' && (
                          sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSort('expected_date')}
                      >
                        Expected Date
                        {sortField === 'expected_date' && (
                          sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSort('metal_type')}
                      >
                        Metal Type
                        {sortField === 'metal_type' && (
                          sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSort('amount')}
                      >
                        Amount
                        {sortField === 'amount' && (
                          sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                        )}
                      </th>
                      <th>Tracking</th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {sortField === 'status' && (
                          sortDirection === 'asc' ? <ArrowUp className="ms-1" /> : <ArrowDown className="ms-1" />
                        )}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDeliveries.map((delivery) => (
                      <tr key={delivery.id}>
                        <td>{delivery.id}</td>
                        <td>{new Date(delivery.expected_date).toLocaleDateString()}</td>
                        <td className="text-capitalize">{delivery.metal_type}</td>
                        <td>{delivery.amount} {delivery.unit}</td>
                        <td>
                          {delivery.tracking_number ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => window.open(`https://track.carrier.com/${delivery.tracking_number}`, '_blank')}
                            >
                              {delivery.tracking_number}
                            </Button>
                          ) : (
                            <span className="text-muted">Not available</span>
                          )}
                        </td>
                        <td>{getStatusBadge(delivery.status)}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate(`/deliveries/${delivery.id}`)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <Truck size={48} className="text-muted" />
                  </div>
                  <h5>No deliveries found</h5>
                  <p className="text-muted">
                    {filterStatus || searchTerm ?
                      'Try changing your search or filter criteria' :
                      'Your delivery history will appear here'}
                  </p>
                </div>
              )}
            </Card.Body>

            {filteredDeliveries.length > 0 && (
              <Card.Footer className="bg-white d-flex justify-content-between align-items-center">
                <div>
                  Showing {Math.min(itemsPerPage, filteredDeliveries.length)} of {filteredDeliveries.length} deliveries
                </div>
                <Pagination className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
                  {paginationItems}
                  <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </Card.Footer>
            )}
          </Card>

          <div className="mt-4 text-center">
            <Button
              variant="outline-primary"
              onClick={() => {
                // Reset filters and search
                setFilterStatus('');
                setSearchTerm('');
                setCurrentPage(1);
                // Refresh data
                dispatch(fetchDeliveries());
              }}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh Deliveries
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default Deliveries;
